import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router-dom'

import { useReactiveVar } from '@apollo/client'
import * as Sentry from '@sentry/browser'
import { addHours, format, setHours, setMinutes } from 'date-fns'
import { Analytics, AppointmentTypeFilter } from 'shared-components'
import { Divider } from 'shared-components'
import { DateSelectionSkeleton } from 'shared-components'
import { RequestAppointmentButton } from 'shared-components'
import { ScrollHelper } from 'shared-components'
import { Subheader } from 'shared-components'

import { Calendar } from '../../components/Calendar'
import { FirstAvailable } from '../../components/FirstAvailable'
import { TimePicker } from '../../components/TimePicker'

import {
  alertType,
  appointmentDateTime,
  availabilitySlotIdVar,
  message,
  providerNameVar,
  selectedAppointmentTypeName,
  store,
} from '../../cache'
import { useAvailability } from '../../hooks/useAvailability'
import { getFilteredAppointments } from '../../utils/getFilteredAppointments'
import { getFirstAvailable } from '../../utils/getFirstAvailable'

import './style.css'

export const DatetimeSelect = () => {
  const { locationId, partnerId } = useReactiveVar(store)
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)
  const [firstAvailableDay, setFirstAvailableDay] = useState(null)
  const [availableTimes, setAvailableTimes] = useState([])
  const navigate = useNavigate()
  const { t } = useTranslation()
  const appointmentTakenMsg = t(
    'Oops! That appointment time is no longer available. Please choose a new date or time.'
  )
  const invalidDateMessage = t(
    'Oops! It looks like there is an issue with your request, please try using a different browser or try again later. If this issue persists please contact support@intelibly.com.'
  )

  const [currentDate, setCurrentDate] = useState(new Date())
  const {
    appointmentTypes,
    appointments,
    serviceUnavailable,
    invalidStartDate,
    uncaughtError,
    loading,
    error,
  } = useAvailability(60, currentDate)

  const [didLoad, setDidLoad] = useState(false)

  useEffect(() => {
    if (!loading) {
      setDidLoad(true)
    }
  }, [loading])

  useEffect(() => {
    if (didLoad && appointmentTypes.length === 0 && appointments.length === 0) {
      navigate('/request-appointment')
    }
  }, [didLoad, appointmentTypes, appointments, navigate])

  useEffect(() => {
    if (serviceUnavailable) {
      navigate('/request-appointment')
      Sentry.captureMessage(
        'Vendor Service Unavailable: ${data.pmsAvailableAppointments.message'
      )
    }
  }, [serviceUnavailable, navigate])

  useEffect(() => {
    if (invalidStartDate) {
      alertType('warning')
      message(invalidDateMessage)
      Sentry.captureMessage(
        'Invalid Start Date: ${data.pmsAvailableAppointments.message}'
      )
    }
  }, [invalidStartDate])

  useEffect(() => {
    if (uncaughtError) {
      Sentry.captureMessage('Uncaught error in PMS')
    }
  })

  const setInitialState = () => {
    setSelectedDay(null)
    setAvailableTimes([])
  }

  const handleCalendarDateChange = newDate => {
    setInitialState()
    setCurrentDate(newDate)
  }

  useEffect(() => {
    if (appointmentTypes.length > 0 && !selectedAppointmentType) {
      const defaultType = appointmentTypes.find(type => type.isDefault)
      const appointmentType = defaultType || appointmentTypes[0]
      onAppointmentTypeSelected(appointmentType.name)
    }
  }, [appointmentTypes, selectedAppointmentType])

  const onAppointmentTypeSelected = appointmentTypeName => {
    const appointmentType = appointmentTypes.find(
      type => type.name === appointmentTypeName
    )
    setSelectedAppointmentType(appointmentType)
    selectedAppointmentTypeName(appointmentType.name)
    setInitialState()
  }

  const onDaySelected = (day, availability = { times: [] }) => {
    if (day == null || day == undefined) {
      setInitialState()
    } else {
      setSelectedDay(new Date(day))
      setAvailableTimes(availability.times)
      Analytics.dateSelected({
        locationId,
        partnerId,
        dayOfWeek: format(day, 'EEEE'),
        month: format(day, 'MMMM'),
      })
    }
  }

  const onTimeSelected = timeSlot => {
    const [hours, minutes] = timeSlot.startTime.split(':')
    const datetime = setMinutes(setHours(selectedDay, hours), minutes)

    if (isTimeInThePast(datetime)) {
      alertType('warning')
      message(appointmentTakenMsg)
      navigate('/')
      return
    }

    appointmentDateTime(datetime)
    availabilitySlotIdVar(timeSlot.id)
    providerNameVar(timeSlot.provider)
    navigate('/book-appointment')
    Analytics.timeSelected({ locationId, partnerId, time: timeSlot.startTime })
  }

  const isTimeInThePast = datetime => {
    const now = addHours(new Date(), 1)
    return datetime <= now
  }

  const filteredAppointments = useMemo(() => {
    return getFilteredAppointments(appointments, selectedAppointmentType)
  }, [appointments, selectedAppointmentType])

  useEffect(() => {
    const firstAvailable = getFirstAvailable(filteredAppointments)
    setFirstAvailableDay(firstAvailable)

    if (!firstAvailable) {
      onDaySelected(null)
    }
  }, [filteredAppointments])

  return (
    <div className="datetime-select">
      <div className="body">
        <Suspense fallback={<DateSelectionSkeleton />}>
          {appointmentTypes.length > 0 ? (
            <div className="treatment-selection">
              <AppointmentTypeFilter
                appointmentTypes={appointmentTypes}
                onAppointmentTypeSelected={onAppointmentTypeSelected}
                selectedAppointmentType={selectedAppointmentType}
              />
            </div>
          ) : null}
          <div className="date-selection">
            <Subheader text={t('Select a date')} />
            <FirstAvailable
              firstAvailableDay={firstAvailableDay}
              onDaySelected={onDaySelected}
              onChangeDate={handleCalendarDateChange}
            />
            <Calendar
              selectedDay={selectedDay}
              onDaySelected={onDaySelected}
              selectedAppointmentType={selectedAppointmentType}
              currentDate={currentDate}
              onChangeDate={handleCalendarDateChange}
              appointments={filteredAppointments}
            />
          </div>
          {availableTimes.length > 0 ? (
            <div className="time-selection">
              <Subheader text={t('Select a time')} />
              <TimePicker
                onTimeSelected={onTimeSelected}
                availableTimes={availableTimes}
              />
            </div>
          ) : null}
        </Suspense>
        <Divider variant="secondary" />
        <RequestAppointmentButton />
        <ScrollHelper />
      </div>
    </div>
  )
}
